import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import dayjs from "dayjs"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import { Avatar } from "../../../components/DashboardComponents/AvatarGroup"
import ListingTabs from "../../../components/Listing/ListingTabs"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown, {
  toSingleFilterValue,
} from "../../../components/Listing/FilterDropdown"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import { ICONS } from "../../../constants/icons"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import {
  ADMIN_SESSION_LIST_WITH,
  SESSION_SOURCE_OPTIONS,
  SESSION_STATUS_OPTIONS,
  SESSION_TYPE_TABS,
  SESSION_VISIBILITY_OPTIONS,
  SOURCE_LABEL,
  STATUS_LABEL,
  TYPE_LABEL,
  VISIBILITY_LABEL,
  mapSessionRow,
  mapSessionsSummaryKpi,
} from "./adminSessionsData"

const PAGE_SIZE = 15

const TOAST_OPTIONS = { transition: Bounce }

/**
 * Admin Sessions listing — GET /api/v1/admin/sessions + summary + export.
 * Optional cityId (prop or ?city_id=) scopes to a Manage City listing.
 */
const AdminSessions = ({
  documentTitle = "Sessions | AMOK . EARTH Admin",
  cityId: cityIdProp,
  showKpi = true,
  detailBasePath = "/admin/sessions",
  defaultTypeTab = "all",
}) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const cityId = cityIdProp || searchParams.get("city_id") || ""
  const [activeTab, setActiveTab] = useState(defaultTypeTab)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [sourceFilter, setSourceFilter] = useState("")
  const [visibilityFilter, setVisibilityFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const [kpiCards, setKpiCards] = useState(() => mapSessionsSummaryKpi())
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [isKpiLoading, setIsKpiLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, statusFilter, sourceFilter, visibilityFilter, cityId])

  const fetchSummary = useCallback(async () => {
    if (!showKpi) {
      setIsKpiLoading(false)
      return
    }
    setIsKpiLoading(true)
    try {
      const params = {}
      if (cityId) params.city_id = cityId
      const response = await get(endpoints.getAdminSessionsSummary, { params })
      if (response?.data?.success) {
        setKpiCards(mapSessionsSummaryKpi(response.data.result || {}))
      } else {
        toastApiError(response, "Failed to load sessions summary")
      }
    } catch (err) {
      toastApiError(err, "Failed to load sessions summary")
    } finally {
      setIsKpiLoading(false)
    }
  }, [cityId, showKpi])

  const buildListParams = useCallback(
    ({ includePage = true } = {}) => {
      const params = {
        "with[]": ADMIN_SESSION_LIST_WITH,
      }

      if (includePage) {
        params.page = currentPage
        params.page_size = pageSize
      }

      if (debouncedSearch) params.search = debouncedSearch
      if (activeTab !== "all") params.type = activeTab

      if (statusFilter) params.status = statusFilter
      if (sourceFilter) params.source_type = sourceFilter
      if (visibilityFilter) params.visibility_type = visibilityFilter

      if (cityId) params.city_id = cityId

      return params
    },
    [
      activeTab,
      cityId,
      currentPage,
      pageSize,
      debouncedSearch,
      statusFilter,
      sourceFilter,
      visibilityFilter,
    ],
  )

  const fetchSessions = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminSessions, {
        params: buildListParams(),
      })

      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapSessionRow)

        setRows(mapped)
        setTotalItems(result.total ?? mapped.length)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total ?? mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load sessions")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load sessions")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, currentPage])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  const handleExport = async () => {
    if (isExporting) return

    setIsExporting(true)
    try {
      const response = await get(endpoints.exportAdminSessions, {
        params: buildListParams({ includePage: false }),
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export sessions")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export sessions"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, TOAST_OPTIONS)
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `sessions-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Sessions exported.", TOAST_OPTIONS)
    } catch (err) {
      toastApiError(err, "Failed to export sessions")
    } finally {
      setIsExporting(false)
    }
  }

  const handleViewDetail = row => {
    if (!row?.id) return
    navigate(`${detailBasePath}/${row.id}`)
  }

  const skeletonRows = useMemo(
    () => Array.from({ length: 6 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const safePage = Math.min(
    currentPage,
    Math.max(1, Math.ceil(totalItems / pageSize) || 1),
  )

  return (
    <div className="admin-page admin-sessions">
      {showKpi ? (
        <div className="admin-sessions__kpi-grid">
          {kpiCards.map(card => (
            <DashboardCard
              key={card.key}
              title={card.title}
              tone={card.tone}
              className="admin-sessions__kpi-card"
              fullHeight
            >
              {isKpiLoading ? (
                <span className="admin-sessions__skeleton admin-sessions__skeleton--kpi" />
              ) : (
                <p className="admin-sessions__kpi-value">{card.value}</p>
              )}
            </DashboardCard>
          ))}
        </div>
      ) : null}

      <div className="admin-sessions__toolbar">
        <div className="admin-sessions__toolbar-left">
          <h1 className="admin-sessions__title">Sessions</h1>
          <ListingTabs
            items={SESSION_TYPE_TABS}
            activeKey={activeTab}
            onChange={setActiveTab}
          />
        </div>
        <div className="admin-sessions__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by session..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-sessions__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "status",
                type: "select",
                label: "Status",
                options: SESSION_STATUS_OPTIONS,
                value: statusFilter,
                defaultValue: "",
                onChange: next => {
                  setStatusFilter(toSingleFilterValue(next))
                },
              },
              {
                key: "source_type",
                type: "select",
                label: "Source Type",
                options: SESSION_SOURCE_OPTIONS,
                value: sourceFilter,
                defaultValue: "",
                onChange: next => {
                  setSourceFilter(toSingleFilterValue(next))
                },
              },
              {
                key: "visibility_type",
                type: "select",
                label: "Visibility Type",
                options: SESSION_VISIBILITY_OPTIONS,
                value: visibilityFilter,
                defaultValue: "",
                onChange: next => {
                  setVisibilityFilter(toSingleFilterValue(next))
                },
              },
            ]}
          />
          <ExportLogButton
            entityLabel="sessions"
            onClick={handleExport}
            loading={isExporting}
          />
        </div>
      </div>

      <div className="admin-sessions__table-wrapper" aria-busy={isLoading}>
        <div className="admin-sessions__table" role="table">
          <div
            className="admin-sessions__table-row admin-sessions__table-row--head"
            role="row"
          >
            <div role="columnheader">Session Name</div>
            <div role="columnheader">Session Type</div>
            <div role="columnheader">Visibility Type</div>
            <div role="columnheader">Source Type</div>
            <div role="columnheader">Task / City</div>
            <div role="columnheader">Creator</div>
            <div role="columnheader">Total Logged Hours</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Date</div>
            <div role="columnheader" className="admin-sessions__actions-head">
              Actions
            </div>
          </div>

          {isLoading
            ? skeletonRows.map(key => (
                <div
                  key={key}
                  className="admin-sessions__table-row admin-sessions__table-row--skeleton"
                  role="row"
                >
                  <div className="admin-sessions__skeleton" />
                  <div className="admin-sessions__skeleton" />
                  <div className="admin-sessions__skeleton" />
                  <div className="admin-sessions__skeleton" />
                  <div className="admin-sessions__skeleton" />
                  <div className="admin-sessions__skeleton" />
                  <div className="admin-sessions__skeleton" />
                  <div className="admin-sessions__skeleton" />
                  <div className="admin-sessions__skeleton" />
                  <div className="admin-sessions__skeleton" />
                </div>
              ))
            : rows.map(row => (
                <div
                  key={row.id}
                  className="admin-sessions__table-row"
                  role="row"
                >
                  <div className="admin-sessions__user" role="cell">
                    <Avatar
                      src={row.memberAvatar}
                      name={row.title}
                      size="sm"
                      className="admin-sessions__avatar"
                    />
                    <TruncatedText className="admin-sessions__name">
                      {row.title}
                    </TruncatedText>
                  </div>
                  <div className="admin-sessions__type-cell" role="cell">
                    <span
                      className={`admin-sessions__type admin-sessions__type--${row.type}`}
                    >
                      {TYPE_LABEL[row.type] || row.type}
                    </span>
                  </div>
                  <div className="admin-sessions__type-cell" role="cell">
                    {row.visibilityType ? (
                      <span
                        className={`admin-sessions__visibility admin-sessions__visibility--${
                          row.visibilityType === "global"
                            ? "public"
                            : row.visibilityType
                        }`}
                      >
                        {VISIBILITY_LABEL[row.visibilityType] ||
                          row.visibilityType}
                      </span>
                    ) : (
                      "--"
                    )}
                  </div>
                  <div className="admin-sessions__type-cell" role="cell">
                    {row.sourceType ? (
                      <span
                        className={`admin-sessions__source admin-sessions__source--${row.sourceType}`}
                      >
                        {SOURCE_LABEL[row.sourceType] || row.sourceType}
                      </span>
                    ) : (
                      "--"
                    )}
                  </div>
                  <TruncatedText
                    as="div"
                    className="admin-sessions__cell"
                    role="cell"
                  >
                    {row.project}
                  </TruncatedText>
                  <TruncatedText
                    as="div"
                    className="admin-sessions__cell"
                    role="cell"
                  >
                    {row.member}
                  </TruncatedText>
                  <TruncatedText
                    as="div"
                    className="admin-sessions__cell"
                    role="cell"
                  >
                    {row.loggedHours}
                  </TruncatedText>
                  <div className="admin-sessions__status-cell" role="cell">
                    <span
                      className={`admin-sessions__status admin-sessions__status--${row.status}`}
                    >
                      <span
                        className="admin-sessions__status-dot"
                        aria-hidden="true"
                      />
                      {STATUS_LABEL[row.status] || row.status}
                    </span>
                  </div>
                  <TruncatedText
                    as="div"
                    className="admin-sessions__cell"
                    role="cell"
                  >
                    {row.date}
                  </TruncatedText>
                  <div className="admin-sessions__actions" role="cell">
                    <CustomButton
                      variant="transparent"
                      size="sm"
                      type="button"
                      trailingIconSrc={ICONS.arrowRight12}
                      trailingIconAlt=""
                      onClick={() => handleViewDetail(row)}
                    >
                      View Detail
                    </CustomButton>
                  </div>
                </div>
              ))}

          {!isLoading && rows.length === 0 && (
            <div className="admin-sessions__empty">
              <EmptyState
                title={error ? "Couldn’t load sessions" : "No sessions found"}
                description={
                  error || "Try adjusting your search or filters."
                }
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={safePage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Sessions"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-sessions__pagination"
      />
    </div>
  )
}

export default AdminSessions

AdminSessions.propTypes = {
  documentTitle: PropTypes.string,
  cityId: PropTypes.string,
  showKpi: PropTypes.bool,
  detailBasePath: PropTypes.string,
  defaultTypeTab: PropTypes.string,
}

AdminSessions.defaultProps = {
  documentTitle: "Sessions | AMOK . EARTH Admin",
  cityId: undefined,
  showKpi: true,
  detailBasePath: "/admin/sessions",
  defaultTypeTab: "all",
}
